import * as THREE from "three"
import { Vector3 } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js"
import DragControls from "../library/DragControls.js"
import ModelBase from "./model_base";

export class Table extends ModelBase {
    constructor(parent, settings) {
        super(parent);

        this._name = 'Table';

        this._info = null;
        this._simulator = parent;

        this._chairs = []
        this._chairs_by_bound = [];
        this._chair_models = []

        this._positionsForAdd = [] // 테이블 주위로 의자를 놓을 수 있는 위치 (left, top, bottom ... )
        this._positionsCustom = false;//false;
        this._positionIndexForAdd = 'top', // 테이블 주위로 의자를 놓을 때 가장 먼저 놓이는 위치

        this.settings = Object.assign(
            {
                viewChairBound: false, // 차일드(의자)가 이동할 수 있는 영역을 볼 수 있는지 설정
                marginChairOnBound: 0.05, // 의자 영역의 최소 마진
            },  
            settings
        );

        this._last_model_filename = null;
        
        this._init()
        this._initControls()
    }

    update(key, value) {
        switch(key) {
            case 'viewChairBound':
                this._bound_models.forEach((m) => m.visible = value);
            break;
        }
    }

    delete() {
        super.delete();
        this.removeAllChair();
    }

    showChairs() {
        this._chair_models.forEach((m) => m.visible = true);
    }

    hideChairs() {
        this._chair_models.forEach((m) => m.visible = false);
    }

    addChair(chair) {
        this._chairs.push(chair);

        let chairModel = chair.getModel();
        if(chairModel) {
            this._parent._scene.add(chairModel)
            this._chair_models.push(chairModel);
            return this._calculatorChair(chair);
        } else {
            chair.once('loadModel', (chair) => { // 로딩 후 다시 적용
                chairModel = chair.getModel();

                this._parent._scene.add(chairModel)
                this._chair_models.push(chairModel);
                return this._calculatorChair(chair);
            })
        }
    }

    getChair(key) {
        if(this._chairs) {
            if(typeof key == 'number'){
                return this._chairs[key] ? this._chairs[key] : false;
            } else if (typeof key == 'string') {
                var index = this._chairs.findIndex(function (obj) {
                    return obj._info.name === key;
                });

                // console.log(this._chairs);
                // console.log('delete index', index, key);

                return this._chairs[index] ? this._chairs[index] : false;
            }
        }

        return false;
    }

    getChairs() {
        return this._chairs;
    }

    removeAllChair() {
        if(this._chairs && this._chairs.length) {
            for(const chair of this._chairs) {
                chair.delete();
            }
            
            this._chairs = []
            this._chair_models = []
            this._chairs_by_bound = [];

            this._positionsCustom = false;
            this._positionIndexForAdd = 'top';
        }
    }

    removeChair(object, newObject, removeGlobal) {
        if(!object) return false;

        if(typeof(newObject) === 'undefined') newObject = null;
        if(typeof(removeGlobal) === 'undefined') removeGlobal = true;
        
        let chair_model = null;
        let chair = null;
        
        if(object.getType() === 'chair') {
            chair = object;
            chair_model = object.getModel();
        } else {
            chair_model = this._parent._scene.getObjectById(object.id);
            chair = chair_model.__parent;
        }

        // console.log('remove', object, chair_model);

        if(chair_model) {
            if(removeGlobal) this._parent._scene.remove( chair_model );

            // 기존 바운드 목록에서 삭제하기
            // dragControls 에서 삭제하기
            const dragIndex = this._chair_models.indexOf(chair_model);
            if(dragIndex >= 0) {
                this._chair_models.splice(dragIndex, 1);
            }

            // 새로운 모델로 변경하는 것이 아니라면 바운드 영역에서 바로 삭제
            if(!newObject) {
                const bound = chair.getBoundPosition();
                if(bound) {
                    if(this._chairs_by_bound[bound]) {
                        const boundIndex = this._chairs_by_bound[bound].indexOf(chair);
                        if(boundIndex >= 0) {
                            this._chairs_by_bound[bound].splice(boundIndex, 1);
                        }
                    }
                }
            }
        }

        if(chair && removeGlobal) {
            this._simulator._onModelRemove(chair);

            const chairIndex = this._chairs.indexOf(chair);
            if(chairIndex >= 0) {
                this._chairs.splice(chairIndex, 1);
            }

            chair.delete();
        }

        if(newObject) { // 교대할 모델이 있다면 변경 작업
            // NTODO : 기존 바운드의 리스트에 그대로 추가하기

            // 화면에 추가하기
            this._parent._scene.add(newObject);

            // dragControls 에 추가하기
            this._chair_models.push(newObject);
        }
    }

    getPosition() {
        return this._model.position
    }

    _init() {
        this._model = false
        this._bound_models = [];
        this._bound = false
        this._drag_control = false
        this._drag_last_key = false
        this._drag_last_object = false
        this._drag_start = new THREE.Vector2();
        this._drag_diff = new THREE.Vector2()
        this._drag_mouse = new THREE.Vector2()
        this._raycaster = new THREE.Raycaster()
    }

    _initControls() {
        window.addEventListener(
            "pointermove",
            (event) => {
                this._pointermove(event)
            },
            false
        )

        this._drag_control = new DragControls(
            this._chair_models,
            this._parent._camera,
            this._parent._renderer.domElement
        )

        this._drag_control.enabled = false; // 드래그 허용
        
        if(this._drag_control.enabled) {
            this._drag_control.transformGroup = true;
            this._drag_control.addEventListener("dragstart", (event) => {
                const chair = this._getParentModel(event.object);
                const object = event.object;

                chair.saveBoundPosition();

                this._drag_start.x = this._drag_mouse.x;
                this._drag_start.y = this._drag_mouse.y;

                this._drag_diff.x = this._drag_mouse.x - object.position.x
                this._drag_diff.y = this._drag_mouse.y - object.position.z

                this._parent._controls.enabled = false

                this.trigger('dragStartChild', this._getParentModel(event.object)); // 이벤트 : 의자 드래그 시작
            })
            this._drag_control.addEventListener("drag", (event) => {
                this._checkBoundObject(event.object)
            })

            this._drag_control.addEventListener("dragend", (event) => {
                const chair = this._getParentModel(event.object);

                this._parent._controls.enabled = true

                if(this._drag_mouse.distanceTo(this._drag_start) < 0.01) { // 클릭위치가 큰 변화가 없다면 팝업 메뉴 보이기
                    this.trigger('clickChild', chair); // 이벤트 : 의자 클릭
                } else {
                    this.trigger('dragEndChild', chair); // 이벤트 : 의자 드래그 끝

                    this._positionsCustom = true; // 의자의 위치가 변경되었으므로 커스텀 위치가 되었음
                }

                const saveBoundPosition = chair.getSaveBoundPosition();
                const boundPosition = chair.getBoundPosition();

                if(saveBoundPosition !== boundPosition) {
                    this._moveBound(chair, saveBoundPosition, boundPosition)
                }
            })
        }
    }

    _pointermove(event) {
        if(this._drag_control.enabled) {
            const mouse = new THREE.Vector2(
                (event.clientX / window.innerWidth) * 2 - 1,
                -(event.clientY / window.innerHeight) * 2 + 1
            )

            this._raycaster.setFromCamera(mouse, this._parent._camera)
            const intersects = this._raycaster.intersectObjects(
                [this._parent._floor],
                false
            )

            if (intersects && intersects.length) {
                this._drag_mouse.set(intersects[0].point.x, intersects[0].point.z)
            }
        }
    }

    _checkBoundObject(model) {
        let x = this._drag_mouse.x - this._drag_diff.x
        let y = this._drag_mouse.y - this._drag_diff.y

        const chair = model.__parent;

        const modelBound = new THREE.Box3().setFromObject(model)
        const modelSize = new THREE.Vector2()
        modelSize.x = (modelBound.max.x - modelBound.min.x) / 2
        modelSize.y = (modelBound.max.z - modelBound.min.z) / 2

        const founds = {}
        for (const [key, bound] of Object.entries(this._bound)) {
            const currentBox = new THREE.Box2(
                new THREE.Vector2(x - modelSize.x, y - modelSize.y),
                new THREE.Vector2(x + modelSize.x, y + modelSize.y)
            )

            if (currentBox.intersectsBox(bound.cursor)) {
                const intersectSize = currentBox.intersect(bound.cursor)
                founds[key] = {
                    value:
                        (intersectSize.max.x - intersectSize.min.x) *
                        (intersectSize.max.y - intersectSize.min.y),
                    bound: bound,
                }
            }
        }

        let found = null
        let sortable = []
        for (let found_key in founds) {
            sortable.push([found_key, founds[found_key]])
        }

        if (sortable.length > 0) {
            if (sortable.length > 1) {
                sortable.sort(function (a, b) {
                    return b[1].value - a[1].value
                })
            }
            found = sortable[0]
        }

        if (found) {
            x = Math.max(found[1].bound.snap.min.x + modelSize.x, x)
            x = Math.min(found[1].bound.snap.max.x - modelSize.x, x)

            y = Math.max(found[1].bound.snap.min.y + modelSize.y, y)
            y = Math.min(found[1].bound.snap.max.y - modelSize.y, y)

            this.__drag_last_model = found
        } else {
            found = this.__drag_last_model

            if(found) {
                x = Math.max(found[1].bound.snap.min.x + modelSize.x, x)
                x = Math.min(found[1].bound.snap.max.x - modelSize.x, x)

                y = Math.max(found[1].bound.snap.min.y + modelSize.y, y)
                y = Math.min(found[1].bound.snap.max.y - modelSize.y, y)
            }
        }

        model.position.y = 0;//0.79 / 2 // 바닥에 붙어 있도록
        model.position.x = x
        model.position.z = y

        if(found) {
            if(chair.getBoundPosition() !== found[0]) {
                chair.setBoundPosition(found[0]);
            }
        }
    }

    getLastModelFileName() {
        return this._last_model_filename;
    }

    __checkModelInfo(info) {
        if(info && info.filename) return true;
        return false;
    }

    loadModel(info, init = false) {
        if(typeof(info) === 'string') {
            this.loadModelFromJSON(info);
        } else {
            if (this.__checkModelInfo(info)) {
                this._info = info;

                const gltfLoader = new GLTFLoader(this._simulator._manager)
                const dracoLoader = new DRACOLoader(this._simulator._manager);
                dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
                gltfLoader.setDRACOLoader(dracoLoader);
                gltfLoader.load(info.filename, (gltf) => {
                    const obj3d = gltf.scene
                    obj3d.name = info.name

                    if(this._model) this._delete();

                    this._model = obj3d;
                    this._model.__parent = this;
                    this._parent._scene.add(obj3d)

                    // obj3d.castShadow = true
                    // obj3d.receiveShadow = true

                    // if(obj3d.material) obj3d.material.envMap = this._parent._scene.background;
    
                    obj3d.traverse((child) => {
                        child.castShadow = true;
                        // child.receiveShadow = true

                        if (
                            child instanceof THREE.Mesh &&
                            child.material instanceof THREE.MeshStandardMaterial
                        ) {
                            if(child.material.metalness > 0) child.material.envMap = this._simulator._scene._environment;
                            // child.material.envMapIntensity = 0.1
                        }
                    })

                    const modelSize = new THREE.Box3().setFromObject(obj3d);

                    if (info.bound) {
                        this._bound = info.bound
                    } else {
                        // 바운드 영역이 지정되어 있지 않으면 부모 가구의 주의 영역을 해당 영역으로 변환해준다.
                        this._autoBound(modelSize, this._model, info);
                    }

                    if(info.size) {
                        this.setSize(info.size);
                    } else {
                        this.setSize(null);
                    }

                    if(this._bound_models.length > 0) {
                        if(init) {
                            this._resetBound();
                        }

                        this._reposChairsAll();
                    } else {
                        this._setBound(modelSize);
                    }

                    this.trigger('loadModel'); // 이벤트 : 모델 로드 완료
                    this._simulator._onModelLoadSingle(this)
                },
                ( xhr ) => {
                    let percent = Math.round(xhr.loaded / xhr.total * 100 );
                    this._simulator._onModelLoadSinglePrgress(this, percent);
                })
            }
        }
    }
    
    loadModelFromJSON(filename) {
        this._last_model_filename = filename;

        fetch("./json/table/" + filename)
            .then((response) => {
                return response.json()
            })
            .then((json) => {
                this.loadModel(json);
            })
    }

    _autoBound(modelSize, modelInfo = false, info = {}) {
        var is_soft_table = false;
        if(modelInfo && modelInfo.name) {
            if(modelInfo.name.indexOf('soft') >= 0) {
                is_soft_table = true;
            }
        }

        // 가구를 상단에서 보는 것으로 기준으로 왼쪽 오른쪽 상단 하단으로 ..

        const boxSize = 1
        let mx = 0.1,
            my = 0.1,
            mw = 0.1,
            mh = 0.3,
            mxh = 0.2,
            mmx = 0

        const modelSizeForCheck = modelSize.getSize(new THREE.Vector3());
        if(modelSizeForCheck.x < 1 && modelSizeForCheck.y < 1) { // 작은 테이블 용
            mw = 0.1
            mmx = 0.2
            my = 0.6
            mh = 0.1
            mx = 0.3
            mmx = 0.2;
        }

        if(modelSizeForCheck.x >= 2.2) { // 큰 테이블인 경우 좌우 여백을 좀 줄임
            mw = 0.3;
        } else if(modelSizeForCheck.x > 2) {
            mw = 0.2;
        } else if(Math.ceil(modelSizeForCheck.x * 10) / 10 >= 1.8 && is_soft_table) {
            mw = 0.35;
        } else if(Math.ceil(modelSizeForCheck.x * 10) / 10 >= 1.6 && is_soft_table) {
            mw = 0.25;
        }

        // console.log(Math.ceil(modelSizeForCheck.x * 10) / 10, modelSizeForCheck);

        this._bound = {}

        // 왼쪽
        if(info.limit && typeof info.limit.left !== 'undefined' && info.limit.left === false) {
            this._bound.left = false;
        } else {
            let p = info.padding && info.padding.left ? info.padding.left : 0;
            let o = info.offset && info.offset.left ? info.offset.left : 0;

            this._bound.left = {
                cursor: new THREE.Box2(
                    new THREE.Vector2(modelSize.min.x - boxSize, modelSize.min.z),
                    new THREE.Vector2(modelSize.min.x, modelSize.max.z)
                ),

                snap: new THREE.Box2(
                    new THREE.Vector2(modelSize.min.x - mw - mx - o, modelSize.min.z + mxh - mmx - p),
                    new THREE.Vector2(modelSize.min.x + mw, modelSize.max.z - mxh + p)
                ),
            }

            console.log(this._bound.left.snap.getSize(new THREE.Vector2()));
        }

        // 상단
        if(info.limit && typeof info.limit.top !== 'undefined' && info.limit.top === false) {
            this._bound.top = false;
        } else {
            let p = info.padding && info.padding.top ? info.padding.top : 0;
            let o = info.offset && info.offset.top ? info.offset.top : 0;

            this._bound.top = {
                cursor: new THREE.Box2(
                    new THREE.Vector2(modelSize.min.x, modelSize.min.z - boxSize),
                    new THREE.Vector2(modelSize.max.x, modelSize.min.z)
                ),

                snap: new THREE.Box2(
                    new THREE.Vector2(modelSize.min.x + mw + p, modelSize.min.z - mh - my - o),
                    new THREE.Vector2(modelSize.max.x - mw - p, modelSize.min.z + mh)
                ),
            }
        }

        // 오른쪽

        if(info.limit && typeof info.limit.right !== 'undefined' && info.limit.right === false) {
            this._bound.right = false;
        } else {
            let p = info.padding && info.padding.right ? info.padding.right : 0;
            let o = info.offset && info.offset.right ? info.offset.right : 0;

            this._bound.right = {
                cursor: new THREE.Box2(
                    new THREE.Vector2(modelSize.max.x, modelSize.min.z),
                    new THREE.Vector2(modelSize.max.x + boxSize, modelSize.max.z)
                ),

                snap: new THREE.Box2(
                    new THREE.Vector2(modelSize.max.x - mw + o, modelSize.min.z + mxh - mmx - p),
                    new THREE.Vector2(modelSize.max.x + mw + mx, modelSize.max.z - mxh + p)
                ),
            }
        }

        //  하단
        if(info.limit && typeof info.limit.bottom !== 'undefined' && info.limit.bottom === false) {
            this._bound.bottom = false;
        } else {
            let p = info.padding && info.padding.bottom ? info.padding.bottom : 0;
            let o = info.offset && info.offset.bottom ? info.offset.bottom : 0;

            this._bound.bottom = {
                cursor: new THREE.Box2(
                    new THREE.Vector2(modelSize.min.x, modelSize.max.z),
                    new THREE.Vector2(modelSize.max.x, modelSize.max.z + boxSize)
                ),

                snap: new THREE.Box2(
                    new THREE.Vector2(modelSize.min.x + mw + p, modelSize.max.z - mh + o),
                    new THREE.Vector2(modelSize.max.x - mw - p, modelSize.max.z + mh + my)
                ),
            }
        }
    }

    _moveBound(model, from, to) {
        let index = this._chairs_by_bound[from].indexOf(model);
        if(index >= 0) this._chairs_by_bound[from].splice(index, 1);

        index = this._chairs_by_bound[to].indexOf(model);
        if(index < 0) this._chairs_by_bound[to].push(model);
    }

    _resetBound() {
        if(this._bound.top) {
            if(!this._positionsForAdd.includes('top')) {
                this._positionsForAdd.push('top');
            }

            if(typeof(this._chairs_by_bound['top']) === 'undefined') {
                this._chairs_by_bound['top'] = [];
            }
        } else {
            if(this._positionsForAdd.includes('top')) {
                this._positionsForAdd.splice(this._positionsForAdd.indexOf('top'), 1);
            }
            delete this._chairs_by_bound['top'];
        }

        if(this._bound.bottom) {
            if(!this._positionsForAdd.includes('bottom')) {
                this._positionsForAdd.push('bottom');
            }

            if(typeof(this._chairs_by_bound['bottom']) === 'undefined') {
                this._chairs_by_bound['bottom'] = [];
            }
        } else {
            if(this._positionsForAdd.includes('bottom')) {
                this._positionsForAdd.splice(this._positionsForAdd.indexOf('bottom'), 1);
            }
            delete this._chairs_by_bound['bottom'];
        }

        if(this._bound.left) {
            if(!this._positionsForAdd.includes('left')) {
                this._positionsForAdd.push('left');
            }

            if(typeof(this._chairs_by_bound['left']) === 'undefined') {
                this._chairs_by_bound['left'] = [];
            }
        } else {
            if(this._positionsForAdd.includes('left')) {
                this._positionsForAdd.splice(this._positionsForAdd.indexOf('left'), 1);
            }
            delete this._chairs_by_bound['left'];
        }

        if(this._bound.right) {
            if(!this._positionsForAdd.includes('right')) {
                this._positionsForAdd.push('right');
            }

            if(typeof(this._chairs_by_bound['right']) === 'undefined') {
                this._chairs_by_bound['right'] = [];
            }
        } else {
            if(this._positionsForAdd.includes('right')) {
                this._positionsForAdd.splice(this._positionsForAdd.indexOf('right'), 1);
            }
            delete this._chairs_by_bound['right'];
        }
    }

    _setBound(modelSize) {
        // 바운드 영역을 미리 볼 수 있는 plane 생성

        const material = new THREE.MeshBasicMaterial({
            color: 0xffff00,
            side: THREE.DoubleSide
        })

        if(this._bound.top) {
            this._positionsForAdd.push('top');
            this._chairs_by_bound['top'] = [];

            const tSize = this._bound.top.snap.getSize(new THREE.Vector2())
            const topGeometry = new THREE.PlaneGeometry(tSize.x, tSize.y)
            topGeometry.rotateX(-Math.PI / 2)
            const topPlane = new THREE.Mesh(topGeometry, material)
            topPlane.position.z = this._bound.top.snap.max.y - tSize.y/2
            this._parent._scene.add(topPlane)
            this._bound_models.push(topPlane)
        }

        if(this._bound.bottom) {
            this._positionsForAdd.push('bottom');
            this._chairs_by_bound['bottom'] = [];
            
            const bSize = this._bound.bottom.snap.getSize(new THREE.Vector2())
            const bottomGeometry = new THREE.PlaneGeometry(bSize.x, bSize.y)
            bottomGeometry.rotateX(-Math.PI / 2)
            const bottomPlane = new THREE.Mesh(bottomGeometry, material)
            bottomPlane.position.z = this._bound.bottom.snap.min.y + bSize.y/2
            this._parent._scene.add(bottomPlane)
            this._bound_models.push(bottomPlane)
        }

        if(this._bound.left) {
            this._positionsForAdd.push('left');
            this._chairs_by_bound['left'] = [];

            const lSize = this._bound.left.snap.getSize(new THREE.Vector2())
            const leftGeometry = new THREE.PlaneGeometry(lSize.x, lSize.y)
            leftGeometry.rotateX(-Math.PI / 2)
            const leftPlane = new THREE.Mesh(leftGeometry, material)
            leftPlane.position.x = this._bound.left.snap.max.x - lSize.x/2
            this._parent._scene.add(leftPlane)
            this._bound_models.push(leftPlane)
        }

        if(this._bound.right) {
            this._positionsForAdd.push('right');
            this._chairs_by_bound['right'] = [];

            const rSize = this._bound.right.snap.getSize(new THREE.Vector2())
            const rightGeometry = new THREE.PlaneGeometry(rSize.x, rSize.y)
            rightGeometry.rotateX(-Math.PI / 2)
            const rightPlane = new THREE.Mesh(rightGeometry, material)
            rightPlane.position.x = this._bound.right.snap.min.x + rSize.x/2
            this._parent._scene.add(rightPlane)
            this._bound_models.push(rightPlane)
        }

        if(!this.settings.viewChairBound) {
            this.update('viewChairBound', false);
        }
    }

    _nextPositionForAdd(current) {
        if(this._positionsForAdd.length > 0) {
            let key = this._positionsForAdd.indexOf(current);
            if(key >= 0) {
                key ++;
                if(key > this._positionsForAdd.length) {
                    key = 0;
                }
            }

            return this._positionsForAdd[key];
        }

        return false;
    }

    _reposChairsAll() {
        for(const position of this._positionsForAdd) {
            this._reposChairs(position, true);
        }
    }

    _reposChairs(currentPosition, autoDelete) {
        if(typeof(autoDelete) === 'undefined') autoDelete = false;
        
        // 영역의 너비에 따라 그 안의 의자들을 중앙에 배열되도록

        const chairList = this._chairs_by_bound[currentPosition];
        if(!chairList || chairList.length == 0) return false;

        const chairBoundPos = this._bound[currentPosition].snap.min;
        const chairBound = this._bound[currentPosition].snap.getSize(new THREE.Vector2());

        let fullSize = 0;
        let checkSize = 0;
        let toPosition = 0;

        switch(currentPosition) {
            case 'left':
            case 'right':
                checkSize = chairBound.y;
                toPosition = chairBound.x + chairBoundPos.x - (chairBound.x/2);
            break;
            case 'top':
            case 'bottom':
                checkSize = chairBound.x;
                toPosition = chairBound.y + chairBoundPos.y - (chairBound.y/2); 
                // 0.12 => 책상 깊이

                if(currentPosition === 'top') toPosition += 0.12;
                else if(currentPosition === 'bottom') toPosition -= 0.12;
            break;
        }
        

        chairList.forEach((chair) => {
            const size = chair.getDepthSize();
            fullSize += size;
        });


        let marginSize = chairList.length > 1 ? (checkSize - fullSize) / chairList.length : 0;
        fullSize += marginSize * (chairList.length-1);

        if(autoDelete && chairList.length > 1) {
            if(marginSize < 0) {
                const removeChair = chairList.splice(-1);
                this._simulator._onModelAddCancel(removeChair[0]);
                this.removeChair(removeChair[0]);

                fullSize = 0;
                chairList.forEach((chair) => {
                    const size = chair.getDepthSize();
                    fullSize += size;
                });

                marginSize = chairList.length > 1 ? (checkSize - fullSize) / chairList.length : 0;
                fullSize += marginSize * (chairList.length-1);
            }
        }

        let position = (-fullSize/2);
        let movePosition;
        let updatePositionKey;

        // position = position + (chairList.length > 0 ? (chairList[0].getDepthSize()/2) : 0);

        if(['top','bottom'].includes(currentPosition)) {
            updatePositionKey = 'x';
            movePosition = {
                y: 0,
                z: toPosition,
                x: position
            }
        } else {
            updatePositionKey = 'z';
            movePosition = {
                y: 0,
                z: position,
                x: toPosition
            }
        }

        let nextPos = 0;
        chairList.forEach((chair) => {
            let chairSize = chair.getDepthSize()
            let now = Object.assign({}, movePosition);
            now[updatePositionKey] += nextPos + chairSize/2;
            chair.setPosition(now)

            movePosition[updatePositionKey] += nextPos + marginSize;
            
            nextPos = chairSize
        })
    }

    _checkEmptyBound(checkChair = null) { // 바운드 중 비어 있는 곳이 있는지 체크하고 없다면 false 리턴
        let result = false;
        let checkChairSize = 0;

        if(checkChair) {
            checkChairSize = checkChair.getDepthSize();    
        }

        for(let positionIndex = 0 ; positionIndex < this._positionsForAdd.length ; positionIndex ++) {
            let positionKey = this._positionsForAdd[positionIndex];

            // 3개를 한번에 놓지 않고 각각 2개 2개씩 위 아래부터 채우도록
            if(positionKey === 'top' && this._chairs_by_bound['top'].length >= 2 && this._chairs_by_bound['bottom'].length <= 1) {
                positionKey = 'bottom';
            }
            
            const chairList = this._chairs_by_bound[positionKey];
            const chairBound = this._bound[positionKey].snap.getSize(new THREE.Vector2());

            if(this._info.limit && this._info.limit[positionKey] && this._info.limit[positionKey] > 0) {
                if(this._info.limit[positionKey] <= chairList.length) {
                    continue;
                }
            }
            
            if(chairList) {
                let fullSize = 0;
                let checkSize = 0;
        
                switch(positionKey) {
                    case 'left':
                    case 'right':
                        checkSize = chairBound.y;
                    break;
                    case 'top':
                    case 'bottom':
                        checkSize = chairBound.x;
                    break;
                }

                console.log(positionKey, checkSize);
                
                chairList.forEach((chair) => {
                    fullSize += chair.getDepthSize();
                })

                fullSize += checkChairSize;

                if(positionKey === 'left') {
                    console.log('left', fullSize, checkSize);
                }

                if(fullSize + (this.settings.marginChairOnBound*(chairList.length-1)) <  checkSize) {
                    result = positionKey;
                    break;
                }
            } else {
                result = positionKey;
                break;
            }
        }

        return result;
    }

    _addChair(currentPosition, currentChair) {
        if(!currentPosition) { // 다음에 놓일 위치가 없다면 에러
            currentPosition = this._checkEmptyBound(currentChair);
            if(!currentPosition) {
                return false;
            }
        }

        const chairList = this._chairs_by_bound[currentPosition];
        const chairBound = this._bound[currentPosition].snap.getSize(new THREE.Vector2());

        let fullSize = 0;
        let checkSize = 0;

        switch(currentPosition) {
            case 'left':
            case 'right':
                checkSize = chairBound.y;
            break;
            case 'top':
            case 'bottom':
                checkSize = chairBound.x;
            break;
        }

        if(chairList) {
            chairList.forEach((chair) => {
                fullSize += chair.getDepthSize();
            })
        }

        fullSize += currentChair.getDepthSize();

        console.log(currentPosition, fullSize, checkSize);

        if(!chairList || (fullSize + (this.settings.marginChairOnBound*(chairList.length-1)) <  checkSize)) {
            currentChair.setBoundPosition(currentPosition);
            this._chairs_by_bound[currentPosition].push(currentChair);

            this._reposChairs(currentPosition);

            return true;
        }

        // console.log('의자 추가하지 못함')

        return false;
    }

    // 위치를 비어 있는 영역에 넣기
    _calculatorChair(chair) {
        console.log(this._positionsForAdd);
        if(this._positionsForAdd.length > 0) {
            // if(this._positionsCustom)
            {
                this._positionIndexForAdd = this._checkEmptyBound(chair);
            }

            // console.log(this._positionIndexForAdd);
            
            let chairPosition = this._addChair(this._positionIndexForAdd, chair);
            
            if(chairPosition !== false) {
                // console.log('추가함 / ' + this._positionIndexForAdd)
                return true;
            } else {
                // console.log('추가 할 수 없음 / ' + this._positionIndexForAdd)

                this._positionIndexForAdd = this._nextPositionForAdd(this._positionIndexForAdd);
                // console.log('위치 이동 / ' + this._positionIndexForAdd);

                if(this._positionIndexForAdd) {
                    if(!this._addChair(this._positionIndexForAdd, chair)) {
                        this._simulator._onModelAddCancel(chair);
                        this.removeChair(chair);
                        return false;
                    }
                } else {
                    // 영역안에 추가할 수 없으면 그 다음 영역으로 ..
                    // 그 모든 영역에 추가할 수 없다면 추가 종료 (현재는 일단 추가하기 때문에 0,0,0에 위치함)
                    this._simulator._onModelAddCancel(chair);
                    this.removeChair(chair);
                    return false;
                }
            }
        }

        return true;
    }
}
